import { MoreVert } from '@mui/icons-material';
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { t } from 'i18next';
import { MouseEvent, ReactNode, useState } from 'react';
import { useMounted } from '../../hooks/useMounted';
import { stopPropagationHandler } from '../../utils/handlers';

type Handler = () => void | Promise<void>;

export type MenuCellAction = {
  label: ReactNode;
  isActive: () => Promise<boolean>;
  handler: Handler;
};

export type MenuCellProps = {
  actions: MenuCellAction[];
  tabIndex: 0 | -1;
};

export const MenuCell = ({ actions, tabIndex }: MenuCellProps) => {
  const mountedRef = useMounted();
  const [loading, setLoading] = useState(false);
  const [activeElements, setActiveElements] = useState<Map<number, boolean>>(new Map());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);

    setLoading(true);
    const newActiveElements = new Map();

    Promise.all(
      actions.map(({ isActive }, idx) =>
        isActive().then((active) => {
          newActiveElements.set(idx, active);
        })
      )
    )
      .then(() => {
        if (!mountedRef.current) {
          return;
        }

        setActiveElements(newActiveElements);
        setLoading(false);
      })
      .catch(() => {
        if (!mountedRef.current) {
          return;
        }

        setActiveElements(newActiveElements);
        setLoading(false);
      });
  };

  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleItemClick = async (event: MouseEvent<HTMLLIElement>, handler: Handler) => {
    event.stopPropagation();
    setAnchorEl(null);
    await handler();
  };

  const hasAnyAvailableActions = [...activeElements.values()].filter((v) => v).length > 0;

  return (
    <>
      <IconButton onClick={handleOpen} title={t('a11y.general.actionDropDown')} tabIndex={tabIndex}>
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {loading && (
          <MenuItem key={'loader'} disabled={true} onClick={stopPropagationHandler}>
            <CircularProgress color="inherit" size="1rem" />
          </MenuItem>
        )}
        {!loading && !hasAnyAvailableActions && (
          <MenuItem key={'disabled'} disabled={true} onClick={stopPropagationHandler}>
            {t('noAvailableActions')}
          </MenuItem>
        )}
        {actions.map((a, index) => {
          if (!activeElements.get(index)) {
            return null;
          }

          return (
            <MenuItem key={index} onClick={(e) => handleItemClick(e, a.handler)}>
              {a.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
